// apps/client/src/world/bake.ts — static displays baked into a few draws (spec §8.7: at most ~150 draw calls a frame). A builder's
// model is many small meshes (a fish is body, tail, fins, eyes and pattern; a fossil plaque is slab and relief), and every mesh is a
// draw call (two with shadows). bakeMeshes merges everything opaque under a root into one geometry whose colours live in its vertices
// (one shared material), and transparent parts into one geometry per material.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/** The toy look (toyMaterial's roughness) with the colour in the vertices: every baked opaque mesh shares it. */
export const BAKED_MATERIAL = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.8, metalness: 0 });

/** `mesh.geometry` in `toRoot` space: non-indexed, with only position, normal and (opaque) a colour per vertex. */
function prepared(mesh: THREE.Mesh, toRoot: THREE.Matrix4, colour: THREE.Color | null): THREE.BufferGeometry {
  const src = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry.clone();
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', src.getAttribute('position'));
  if (src.getAttribute('normal')) geo.setAttribute('normal', src.getAttribute('normal'));
  else geo.computeVertexNormals();
  if (colour) {
    const n = geo.getAttribute('position').count, own = src.getAttribute('color'), out = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      const r = own ? own.getX(i) : 1, g = own ? own.getY(i) : 1, b = own ? own.getZ(i) : 1;
      out[i * 3] = r * colour.r; out[i * 3 + 1] = g * colour.g; out[i * 3 + 2] = b * colour.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(out, 3));
  }
  geo.applyMatrix4(toRoot);
  if (toRoot.determinant() < 0) {                                  // a mirrored part: swap two corners of every triangle back to CCW
    for (const name of Object.keys(geo.attributes)) {
      const a = geo.getAttribute(name) as THREE.BufferAttribute, k = a.itemSize, arr = a.array as Float32Array;
      for (let i = 0; i + 2 < a.count; i += 3) for (let c = 0; c < k; c++) {
        const t = arr[(i + 1) * k + c]!; arr[(i + 1) * k + c] = arr[(i + 2) * k + c]!; arr[(i + 2) * k + c] = t;
      }
    }
  }
  return geo;
}

/** Every visible mesh under `root` (in the root's own space, its own transform left out) merged into one opaque vertex-coloured mesh
 *  plus one mesh per transparent material. Contact blobs (`userData.blob`), skinned meshes and anything `skip` rejects stay out.
 *  The source geometries are copied, never changed. The opaque mesh casts a shadow when `castShadow` says so; see-through ones never do. */
export function bakeMeshes(root: THREE.Object3D, opts: { skip?: (m: THREE.Mesh) => boolean; castShadow?: boolean } = {}): THREE.Mesh[] {
  root.updateWorldMatrix(true, true);
  const inv = root.matrixWorld.clone().invert();
  const opaque: THREE.BufferGeometry[] = [], clear = new Map<THREE.Material, THREE.BufferGeometry[]>();
  const visit = (o: THREE.Object3D) => {
    if (!o.visible) return;
    const m = o as THREE.Mesh;
    if (m.isMesh && !(m as THREE.SkinnedMesh).isSkinnedMesh && !m.userData.blob && !Array.isArray(m.material) && !opts.skip?.(m)) {
      const mat = m.material as THREE.MeshStandardMaterial, toRoot = inv.clone().multiply(m.matrixWorld);
      if (mat.transparent) clear.set(mat, [...(clear.get(mat) ?? []), prepared(m, toRoot, null)]);
      else opaque.push(prepared(m, toRoot, mat.color ?? new THREE.Color(1, 1, 1)));
    }
    for (const c of o.children) visit(c);
  };
  visit(root);
  const out: THREE.Mesh[] = [];
  const make = (geos: THREE.BufferGeometry[], mat: THREE.Material, cast: boolean) => {
    const merged = mergeGeometries(geos, false);
    for (const g of geos) g.dispose();
    if (!merged) throw new Error('bakeMeshes: parts with different attributes');
    const mesh = new THREE.Mesh(merged, mat);
    mesh.castShadow = cast;
    mesh.receiveShadow = true;
    mesh.userData.own = true;                                          // the baker made this geometry: its owner frees it
    out.push(mesh);
  };
  if (opaque.length > 0) make(opaque, BAKED_MATERIAL, opts.castShadow ?? false);
  for (const [mat, geos] of clear) make(geos, mat, false);
  return out;
}
